import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Loader2, X } from 'lucide-react';
import {
  ExportFormat,
  ExportType,
  TaskStatusFilter,
} from '../../../../services/taskExport';
import { CampaignTaskType } from '../../../../types';
import { getTaskTypeDescriptor } from '../../../../taskTypes/descriptor';
import { PortalMenu } from '../../../../components/PortalMenu';
import { SearchableDropdown } from '../../../../components/SearchableDropdown';
import { ExportTasksOptions } from './viewModel';

const FORMAT_LABEL: Record<ExportFormat, string> = {
  csv: 'CSV',
  json: 'JSON',
};

interface ExportTasksModalProps {
  campaignName: string;
  campaignTaskTypes: CampaignTaskType[];
  exporting: boolean;
  onClose: () => void;
  onExport: (options: ExportTasksOptions) => Promise<void>;
}

export function ExportTasksModal({
  campaignName,
  campaignTaskTypes,
  exporting,
  onClose,
  onExport,
}: ExportTasksModalProps) {
  const [taskTypeId, setTaskTypeId] = useState<string>(campaignTaskTypes[0]?.taskTypeId ?? '');
  const [statusFilter, setStatusFilter] = useState<TaskStatusFilter>('all');
  const [exportType, setExportType] = useState<ExportType>('all');
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [formatMenuOpen, setFormatMenuOpen] = useState(false);
  const formatTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !exporting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [exporting, onClose]);

  const canRun = taskTypeId.length > 0;

  const run = async (fmt: ExportFormat) => {
    if (!canRun) return;
    try {
      await onExport({ format: fmt, statusFilter, exportType, taskTypeId });
      onClose();
    } catch {
      // error surfaced via viewmodel
    }
  };

  const pickFormatAndRun = (fmt: ExportFormat) => {
    setFormat(fmt);
    setFormatMenuOpen(false);
    void run(fmt);
  };

  const singleType = campaignTaskTypes.length === 1;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="export-tasks-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={() => {
        if (!exporting) onClose();
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg bg-white shadow-xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
          <div>
            <h2 id="export-tasks-title" className="text-lg font-semibold text-gray-900">
              Export tasks
            </h2>
            <p className="mt-0.5 text-xs text-gray-500 truncate">{campaignName}</p>
          </div>
          <button
            type="button"
            aria-label="Close"
            disabled={exporting}
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div>
            {campaignTaskTypes.length === 0 ? (
              <p className="text-xs text-gray-500">
                This campaign has no task types bound — nothing to export.
              </p>
            ) : (
              <SearchableDropdown
                id="export-task-type"
                label="Task type"
                options={campaignTaskTypes.map((taskType) => {
                  const mod = getTaskTypeDescriptor(taskType.taskTypeId);
                  return {
                    value: taskType.taskTypeId,
                    label: `${mod.shortName} (${mod.displayName})`,
                    searchText: `${mod.shortName} ${mod.displayName} ${taskType.taskTypeId}`,
                  };
                })}
                value={taskTypeId}
                disabled={exporting || singleType}
                onChange={(value) => {
                  if (value !== null) setTaskTypeId(value);
                }}
                searchPlaceholder="Search task types"
                preserveOrder
                triggerClassName="text-sm disabled:bg-gray-50 disabled:text-gray-500"
              />
            )}
          </div>

          <div>
            <SearchableDropdown<TaskStatusFilter>
              id="export-status-filter"
              label="Task status"
              options={[
                { value: 'all', label: 'All tasks' },
                { value: 'approvedOrInReview', label: 'Approved or in review' },
                { value: 'approved', label: 'Only approved' },
              ]}
              value={statusFilter}
              disabled={exporting}
              onChange={(value) => {
                if (value !== null) setStatusFilter(value);
              }}
              searchPlaceholder="Search task statuses"
              preserveOrder
              triggerClassName="text-sm disabled:bg-gray-50"
            />
          </div>

          <div>
            <SearchableDropdown<ExportType>
              id="export-type"
              label="Export type"
              options={[
                { value: 'all', label: 'All data' },
                { value: 'answers', label: 'Only answers' },
              ]}
              value={exportType}
              disabled={exporting}
              onChange={(value) => {
                if (value !== null) setExportType(value);
              }}
              searchPlaceholder="Search export types"
              preserveOrder
              triggerClassName="text-sm disabled:bg-gray-50"
            />
            {exportType === 'answers' && (
              <p className="mt-1 text-xs text-gray-500">
                Includes task id, status, expert id, submitted-at, and result columns only.
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={exporting}
            className="rounded-md px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <div className="relative inline-flex">
            <button
              ref={formatTriggerRef}
              type="button"
              onClick={() => void run(format)}
              disabled={exporting || !canRun}
              className="inline-flex items-center gap-1.5 rounded-l-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-hover disabled:opacity-50"
            >
              {exporting && <Loader2 className="h-4 w-4 animate-spin" />}
              Download {FORMAT_LABEL[format]}
            </button>
            <button
              type="button"
              aria-label="Choose format"
              aria-haspopup="menu"
              aria-expanded={formatMenuOpen}
              disabled={exporting || !canRun}
              onClick={() => setFormatMenuOpen((v) => !v)}
              className="inline-flex items-center rounded-r-md border-l border-primary-hover bg-primary px-1.5 py-1.5 text-white hover:bg-primary-hover disabled:opacity-50"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
            {formatMenuOpen && (
              <PortalMenu
                anchorRef={formatTriggerRef}
                onRequestClose={() => setFormatMenuOpen(false)}
                role="menu"
                className="w-40 py-1"
              >
                {(['csv', 'json'] as ExportFormat[]).map((fmt) => (
                  <button
                    key={fmt}
                    type="button"
                    role="menuitem"
                    onClick={() => pickFormatAndRun(fmt)}
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Download {FORMAT_LABEL[fmt]}
                  </button>
                ))}
              </PortalMenu>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
